import { TSessionConfigData } from 'providers/contexts/CosmosContext';

/**
 * Utility functions to check for certain privilege strings
 * fetched via the sessionConfig?.software_privileges in useCosmos();
 *
 * Session Config Response Example
 * /core/session/config/
 * {
 *  "status": "success",
 *  "data": {
 *   "domain": "example.cms.us-corp-dev-3.vip.tndev.net",
 *   "site_name": null,
 *   "bookmark_icon": null,
 *   // ...
 *   "permissions": ["full-access"],
 *   // --- Presence of software_privileges key should indicate entitlement to the feature
 *   // --- and the string array value of the key indicates the privileges available
 *   "software_privileges": {
 *     "wire": [],
 *     "editorial": [],
 *     "pay": [],
 *     "broadcast": [],
 *     "weather": [],
 *     "core": ['cosmos'],
 *     "eedition": ["enhanced_segmenting", "segmenting"],
 *     "access": [],
 *     "calendar": [],
 *   },
 *   // ...
 *  }
 * }
 */

/**
 * Session Config Software Privilges
 */

type TEditorialPrivilege =
  | 'apple_news'
  | 'oovvuu'
  | 'smart_cropping'
  | 'smart_tag'
  | 'syndicaster';

type TEditorialPrivileges = TEditorialPrivilege[];

type TSubscriptionPrivilege = 'brainworks' | 'leefalcon' | 'visiondata' | 'wallet';
type TSubscriptionPrivileges = TSubscriptionPrivilege[];

type TCorePrivilege = 'cosmos' | 'print' | 'field59_upload' | 'premium_video';
type TCorePrivileges = TCorePrivilege[];

type TAccessPrivilege = 'enhanced' | 'synchronize';
type TAccessPrivileges = TAccessPrivilege[];

type TWirePrivilege = 'exchange';
type TWirePrivileges = TWirePrivilege[];

type TPayPrivilege = 'premium';
type TPayPrivileges = TPayPrivilege[];

type TWeatherPrivilege = 'premium';
type TWeatherPrivileges = TWeatherPrivilege[];

type TBroadcastPrivilege = 'pro';
type TBroadcastPrivileges = TBroadcastPrivilege[];

export type TSoftwarePrivilegesType = {
  access?: TAccessPrivileges;
  adowl?: string[];
  analytics?: string[];
  appman?: string[];
  broadcast?: TBroadcastPrivileges;
  business?: string[];
  calendar?: string[];
  classifieds?: string[];
  comments?: string[];
  core?: TCorePrivileges;
  disqus?: string[];
  dmp?: string[];
  editorial?: TEditorialPrivileges;
  eedition?: string[];
  electionsstats?: string[];
  field59?: string[];
  form?: string[];
  insights?: string[];
  indesign?: string[];
  mailinglist?: string[];
  mediaspan?: string[];
  mg2?: string[];
  newscycle?: string[];
  newzware?: string[];
  nowapp?: string[];
  openweb?: string[];
  ott?: string[];
  pay?: TPayPrivileges;
  programs?: string[];
  staticpages?: string[];
  subscription?: TSubscriptionPrivileges;
  taxes?: string[];
  tncms_ad_manager?: string[];
  tributes?: string[];
  upsell_manager?: string[];
  weather?: TWeatherPrivileges;
  wire?: TWirePrivileges;
};

type SoftwarePrivileges = TSessionConfigData['software_privileges'];
type GroupType = NonNullable<keyof NonNullable<SoftwarePrivileges>>;
type KeysType = NonNullable<NonNullable<SoftwarePrivileges>[GroupType]>;
// weaker type to avoid ts collapsing union to 'never' type
type PrivilegeKeys = string[] | undefined;

type PrivilegeConfig = {
  group?: GroupType;
  keys?: KeysType; // keys to check for in the group
  entitlementOnly?: boolean; // only check if top level key exists, no need to check keys array
};

// Configs for privileges / entitlement checks
const f59config: PrivilegeConfig = {
  group: 'core',
  keys: ['field59_upload'],
};

const premiumVideoConfig: PrivilegeConfig = {
  group: 'core',
  keys: ['premium_video'],
};

const dataInsightsConfig: PrivilegeConfig = {
  group: 'insights',
  keys: ['pro'],
};

const subscriptionEntitlementConfig: PrivilegeConfig = {
  group: 'subscription',
  entitlementOnly: true,
};

const printConfig: PrivilegeConfig = {
  group: 'core',
  keys: ['print'],
};

const cosmosConfig: PrivilegeConfig = {
  group: 'core',
  keys: ['cosmos'],
};

const dmpEntitlementConfig: PrivilegeConfig = {
  group: 'dmp',
  entitlementOnly: true,
};

// https://townnews.atlassian.net/browse/CMSADMIN-1281
const mailinglistEntitlementConfig: PrivilegeConfig = {
  group: 'mailinglist',
  entitlementOnly: true,
};

const enhancedAccessConfig: PrivilegeConfig = {
  group: 'access',
  keys: ['enhanced'],
};

const walletConfig: PrivilegeConfig = {
  group: 'subscription',
  keys: ['wallet'],
};

type CheckPrivilegeParams = {
  privileges?: SoftwarePrivileges;
  group?: PrivilegeConfig['group'];
  entitlementOnly?: PrivilegeConfig['entitlementOnly'];
  keys?: PrivilegeConfig['keys'];
};

export const checkPrivileges = ({
  privileges,
  group,
  keys,
  entitlementOnly,
}: CheckPrivilegeParams): boolean => {
  try {
    // Must have privileges object to check against
    if (!privileges) {
      console.warn('[checkPrivileges]: Privileges object missing.');
      return false;
    }

    // Must have group to check against
    if (!group) {
      console.warn('[checkPrivileges]: No group specified.');
      return false;
    }

    // If only checking for entitlement then check
    // that top level key exists in the privileges obj
    if (entitlementOnly) {
      const hasEntitlement = Object.prototype.hasOwnProperty.call(privileges, group);
      return hasEntitlement;
    }

    // Continue to check for string keys in the group
    // Get group and check if it exists in the privileges object
    const privilegeKeys: PrivilegeKeys = privileges[group];

    // Group not present means the site does not have this entitlement
    // installed. Return false silently as this is an expected state.
    if (privilegeKeys === undefined) {
      return false;
    }

    // Ensure array type for privilegeKeys and passed in keys arg
    if (!Array.isArray(privilegeKeys) || !Array.isArray(keys)) {
      console.warn(`[checkPrivileges]: Invalid keys type, expected arrays.`);
      return false;
    }

    // Nothing to check, return false as no priv would be found
    if (!keys?.length || !privilegeKeys?.length) {
      return false;
    }

    // Search for if specified keys are present in the privilege keys
    const check = keys.some((key) => privilegeKeys.includes(key));
    return check;
  } catch (error) {
    console.error('[checkPrivileges] Error: ', error);
    return false;
  }
};

// Privilege and entitlement check functions

// Check for Cosmos Entitlement
export const hasCosmosPrivilege = (sc?: TSessionConfigData): boolean => {
  const privileges = sc?.software_privileges || {};
  return checkPrivileges({
    privileges: privileges,
    group: cosmosConfig.group,
    keys: cosmosConfig.keys,
  });
};

// Check for Field59 Upload
export const hasField59Upload = (sc?: TSessionConfigData): boolean => {
  const privileges = sc?.software_privileges || {};
  return checkPrivileges({
    privileges: privileges,
    group: f59config.group,
    keys: f59config.keys,
  });
};

// Check for Field59 Upload
export const hasPremiumVideo = (sc?: TSessionConfigData): boolean => {
  const privileges = sc?.software_privileges || {};
  return checkPrivileges({
    privileges: privileges,
    group: premiumVideoConfig.group,
    keys: premiumVideoConfig.keys,
  });
};

// CMSADMIN-1462: Dashboards Entitlement / Privilege.
export const hasProDataInsightsPrivilege = (sc?: TSessionConfigData): boolean => {
  const privileges = sc?.software_privileges || {};
  return checkPrivileges({
    privileges: privileges,
    group: dataInsightsConfig.group,
    keys: dataInsightsConfig.keys,
  });
};

// https://townnews.atlassian.net/browse/CMSADMIN-1266
// Check for Subscription Entitlement
export const hasSubscriptionEntitlement = (sc?: TSessionConfigData): boolean => {
  const privileges = sc?.software_privileges || {};
  return checkPrivileges({
    privileges: privileges,
    entitlementOnly: subscriptionEntitlementConfig.entitlementOnly,
    group: subscriptionEntitlementConfig.group,
  });
};

// Check for Print Privilege
export const hasPrintPrivilege = (sc?: TSessionConfigData): boolean => {
  const privileges = sc?.software_privileges || {};
  return checkPrivileges({
    privileges: privileges,
    group: printConfig.group,
    keys: printConfig.keys,
  });
};

// Check for DMP Entitlement
export const hasDmpEntitlement = (sc?: TSessionConfigData): boolean => {
  const privileges = sc?.software_privileges || {};
  return checkPrivileges({
    privileges: privileges,
    entitlementOnly: dmpEntitlementConfig.entitlementOnly,
    group: dmpEntitlementConfig.group,
  });
};

// https://townnews.atlassian.net/browse/CMSADMIN-1281
// Check for Mailinglist Entitlement (Email Reach)
export const hasMailinglistEntitlement = (sc?: TSessionConfigData): boolean => {
  const privileges = sc?.software_privileges || {};
  return checkPrivileges({
    privileges: privileges,
    entitlementOnly: mailinglistEntitlementConfig.entitlementOnly,
    group: mailinglistEntitlementConfig.group,
  });
};

// Check for enhanced access
export const hasEnhancedAccess = (sc?: TSessionConfigData): boolean => {
  const privileges = sc?.software_privileges || {};
  return checkPrivileges({
    privileges: privileges,
    group: enhancedAccessConfig.group,
    keys: enhancedAccessConfig.keys,
  });
};

// Check for Wallet Privilege
export const hasWalletPrivilege = (sc?: TSessionConfigData): boolean => {
  const privileges = sc?.software_privileges || {};
  return checkPrivileges({
    privileges: privileges,
    group: walletConfig.group,
    keys: walletConfig.keys,
  });
};

/**
 * Check if a given app key is available in software_privileges.
 * Returns true for common non-app values like 'static' and 'html'.
 */
export const isAppAvailable = (sc?: TSessionConfigData, app?: string): boolean => {
  try {
    if (!app) return true;
    const appName = String(app).toLowerCase();

    const alwaysAllowed = ['static', 'html', 'any', 'all'];
    if (alwaysAllowed.includes(appName)) return true;

    const privileges = sc?.software_privileges;
    if (!privileges) return false;

    const allowedKeys = Object.keys(privileges || {}).map((k) => k.toLowerCase());
    return allowedKeys.includes(appName);
  } catch (err) {
    console.error('[isAppAvailable] Error:', err);
    return false;
  }
};
